import { useMemo, FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { getDate } from '@utils/convertString';
import { convertPriceToStr } from '@utils/convertPriceToStr';
import { useStore } from '@hooks/useStore';
import { useMyQuery } from '@hooks/useMyQuery';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useOnEntityClick } from '@hooks/useOnEntityClick';
import { useFilterEntity } from '@hooks/useFilterEntity';
import Table from '@components/Core/Table/Table';
import { EntityIcon } from '@components/Core/EntityIcon/EntityIcon';
import { StatusBadge } from '@components/Core/StatusBadge';
import { DiscountBadge } from '@components/Core/DiscountBadge';
import Popup from '@components/Core/Popup/Popup';
import { NoResults } from '@components/Core/NoResults';
import { Button } from '@components/Core/Button';
import { AppLoader } from '@components/Core/AppLoader';
import { StatusPopup } from '@popups/StatusPopup';
import { ConfirmationPopup } from '@popups/ConfirmationPopup';
import SettingsIcon from '@icons/settings_icon.svg?component';
import TrashIcon from '@icons/trash_icon.svg?component';
import { IBusinessListProductData } from '@stores/products/types';
import { ProductsTransFields } from '@pages/Products/types';
import { productsTranslations } from '@pages/Products/translations';
import { ProductsTableTransFields } from './types';
import { productsTableTranslations } from './translations';

const columnHelper = createColumnHelper<IBusinessListProductData>();

const ProductsTable: FC<{ search: string }> = observer(({ search }) => {
  const {
    productsStore: { getProductsList, productsList, deleteProduct },
    mainStore: { selectedCountryCode },
    popupStore: { openPopup, popupName, closePopup },
    alertStore: { showAlert },
  } = useStore();

  const [deletedId, setDeletedId] = useState('');

  const { brandId } = useParams();

  const { isFetching } = useMyQuery({
    queryKey: ['getPointsList'],
    queryFn: () => brandId && getProductsList(brandId),
    enabled: !!selectedCountryCode || !!brandId, // wait until it's defined
    refetchOnMount: 'always',
  });

  const { mutate: deleteProductMutation, isPending } = useMutation({
    mutationFn: deleteProduct,
    mutationKey: ['getAddress'],
    onSuccess: () => {
      showAlert('Product was deleted!', 'success');
      closePopup();
    },
  });

  const { t } = useMyTranslations<
    ProductsTableTransFields | Extract<ProductsTransFields, 'addProduct'>
  >([productsTableTranslations.name, productsTranslations.name]);

  const navigate = useNavigate();

  const data = useFilterEntity(productsList, search);

  const { onClick, popupData } = useOnEntityClick<IBusinessListProductData>();

  const columns: ColumnDef<IBusinessListProductData, any>[] = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: t('name'),
        cell: ({
          row: {
            original,
            original: { imageUrl, name },
          },
        }) => (
          <button
            className="flex items-center w-full gap-x-[12px] bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching || isPending}
          >
            <EntityIcon src={imageUrl} name="brand image" />
            <p className="whitespace-nowrap overflow-hidden text-ellipsis">{name || '—'}</p>
          </button>
        ),
        enableSorting: true,
        meta: { grow: true, className: 'min-w-[180px]' },
      }),
      columnHelper.accessor('price', {
        id: 'price',
        header: t('price'),
        cell: ({
          row: {
            original,
            original: { price, currencyCode },
          },
        }) => (
          <button
            className="w-full bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching || isPending}
          >
            <p className="text-left overflow-hidden text-ellipsis text-nowrap">
              {convertPriceToStr(price, currencyCode)}
            </p>
          </button>
        ),
        enableSorting: true,
        meta: { grow: true, className: 'max-w-[150px] min-w-[75px]' },
      }),
      columnHelper.accessor('discountValue', {
        id: 'discountValue',
        header: t('discountValue'),
        cell: ({
          row: {
            original,
            original: { discountValue, discountType, price, currencyCode },
          },
        }) => {
          return (
            <button
              className="w-full bg-none cursor-pointer"
              onClick={() => onClick(original)}
              disabled={isFetching || isPending}
            >
              {discountValue && discountType ? (
                <DiscountBadge
                  discountValue={discountValue}
                  discountType={discountType}
                  price={price}
                  currencyCode={currencyCode}
                />
              ) : (
                <p className="text-left">-</p>
              )}
            </button>
          );
        },
        enableSorting: true,
        meta: { width: 75, className: 'min-w-[75px]' },
      }),
      columnHelper.accessor('discountPrice', {
        id: 'discountPrice',
        header: t('discountPrice'),
        cell: ({
          row: {
            original,
            original: { discountPrice, currencyCode },
          },
        }) => (
          <button
            className="w-full bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching || isPending}
          >
            <p className="text-left overflow-hidden text-ellipsis text-nowrap">
              {convertPriceToStr(discountPrice, currencyCode)}
            </p>
          </button>
        ),
        enableSorting: true,
        meta: { grow: true, className: 'max-w-[150px] min-w-[75px]' },
      }),
      columnHelper.accessor('startDate', {
        id: 'startDate',
        header: t('startDate'),
        cell: ({
          row: {
            original,
            original: { startDate },
          },
        }) => (
          <button
            className="w-full bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching || isPending}
          >
            <p className="text-left overflow-hidden text-ellipsis text-nowrap">
              {startDate ? getDate(startDate) : '-'}
            </p>
          </button>
        ),
        enableSorting: true,
        meta: { width: 100 },
      }),
      columnHelper.accessor('expiredDate', {
        id: 'expiredDate',
        header: t('expiredDate'),
        cell: ({
          row: {
            original,
            original: { expiredDate },
          },
        }) => (
          <button
            className="w-full bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching || isPending}
          >
            <p className="text-left overflow-hidden text-ellipsis text-nowrap">
              {expiredDate ? getDate(expiredDate) : '-'}
            </p>
          </button>
        ),
        enableSorting: true,
        meta: { width: 100 },
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: t('status'),
        cell: ({
          row: {
            original,
            original: { status },
          },
        }) => (
          <button
            className="w-full h-full bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching || isPending}
          >
            <StatusBadge status={status} />
          </button>
        ),
        enableSorting: true,
        meta: { width: 80 },
      }),
      {
        id: 'option',
        header: t('option'),
        cell: ({
          row: {
            original,
            original: { id, status },
          },
        }) => (
          <div className="w-full h-full flex items-center gap-x-[8px]">
            <button
              className="bg-none cursor-pointer flex-centred"
              onClick={() => onClick(original)}
              disabled={isFetching || isPending}
            >
              <SettingsIcon height={24} width={24} />
            </button>
            {status === 'Active' || status === 'Inactive' ? (
              <button
                className="bg-none cursor-pointer flex-centred"
                onClick={() => {
                  setDeletedId(id);
                  openPopup('confirmation');
                }}
                disabled={isFetching || isFetching}
              >
                <TrashIcon height={24} width={24} />
              </button>
            ) : null}
          </div>
        ),
        enableSorting: false,
        meta: { width: 56 },
      },
    ],
    [isFetching]
  );

  if (isFetching) return <AppLoader />;
  if ((data.length === 0 && search) || productsList?.length === 0)
    return (
      <NoResults>
        <p className="small-text">{t('noResultText')}</p>
        <Button
          text={t('addProduct', { ns: productsTranslations.name })}
          onClick={() => navigate(`/brands/${brandId}/add-product`)}
          styleProp="max-w-[153px]"
        />
      </NoResults>
    );
  return (
    <>
      <Table columns={columns} data={data} />
      <Popup>
        {popupName === 'status' && popupData && <StatusPopup {...popupData} />}
        {popupName === 'confirmation' && (
          <ConfirmationPopup
            title={t('deleteProduct')}
            onAccept={() =>
              deletedId && brandId && deleteProductMutation({ id: deletedId, brandId })
            }
            text={t('confirmationText')}
          />
        )}
      </Popup>
    </>
  );
});

export default ProductsTable;
